// WsRealtimeOrb — Canvas 2D「云球」语音可视化。把实时音频振幅(RMS)映射成一个有机形变 +
// 分层辉光的球体,用于 Realtime 沉浸式语音模式(仿 ChatGPT 语音)。零依赖,自带 rAF,按
// phase 换色/节奏。业务无关 —— 只吃「电平源(analyser refs)」+「相位(phase)」两个入参。
//
// props:
//   micAnalyserRef / outAnalyserRef: React refs holding AnalyserNode|null(输入/输出电平源)
//   phase: 'idle'|'connecting'|'ready'|'listening'|'thinking'|'speaking'
//   size: 画布逻辑尺寸(px,默认 220)

import React from 'react';

// 相位 → 主色(RGB 数组,便于逐帧插值)。listening 冷(用户说话),speaking 蓝(助手说话)。
const PHASE_COLOR = {
  idle: [122, 132, 150],
  connecting: [140, 152, 182],
  ready: [92, 150, 230],
  listening: [56, 202, 158],
  thinking: [150, 128, 240],
  speaking: [70, 140, 255],
};

export default function WsRealtimeOrb({ micAnalyserRef, outAnalyserRef, phase = 'idle', size = 220 }) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(0);
  const levelRef = React.useRef(0); // 平滑后的电平 0..1
  const phaseRef = React.useRef(phase);
  const colorRef = React.useRef(PHASE_COLOR.idle.slice());
  const tRef = React.useRef(0);

  // 相位是离散状态,用 ref 传进 rAF 循环,避免每帧重建闭包。
  React.useEffect(() => { phaseRef.current = phase; }, [phase]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const cx = size / 2, cy = size / 2;
    const baseR = size * 0.23;

    const tmp = new Uint8Array(2048);
    // 从当前相位对应的 analyser 读瞬时 RMS(0..1)。非发声相位返回 0(球回到呼吸态)。
    const readLevel = () => {
      const ph = phaseRef.current;
      const an =
        ph === 'speaking' ? (outAnalyserRef && outAnalyserRef.current)
        : ph === 'listening' ? (micAnalyserRef && micAnalyserRef.current)
        : null;
      if (!an) return 0;
      try {
        const n = Math.min(tmp.length, an.fftSize);
        an.getByteTimeDomainData(tmp.subarray(0, n));
        let sum = 0;
        for (let i = 0; i < n; i++) { const v = (tmp[i] - 128) / 128; sum += v * v; }
        return Math.min(1, Math.sqrt(sum / n) * 3.2); // ×3.2 提亮,正常说话不至于太平
      } catch { return 0; } // context 关闭后读取可能抛,吞掉
    };

    const draw = () => {
      tRef.current += 0.016;
      const t = tRef.current;
      const ph = phaseRef.current;

      // 电平:attack 快(0.5)/ release 慢(0.08)—— 像 VU 表,起跳灵敏、回落顺滑。
      const raw = readLevel();
      const cur = levelRef.current;
      levelRef.current = cur + (raw - cur) * (raw > cur ? 0.5 : 0.08);
      const lvl = levelRef.current;

      // 主色朝目标相位色插值(换相位时平滑过渡,不硬跳)。
      const target = PHASE_COLOR[ph] || PHASE_COLOR.idle;
      const col = colorRef.current;
      for (let i = 0; i < 3; i++) col[i] += (target[i] - col[i]) * 0.06;
      const rgb = `${col[0] | 0}, ${col[1] | 0}, ${col[2] | 0}`;

      ctx.clearRect(0, 0, size, size);

      // 待机呼吸 / 思考脉动 —— 无音频时也让球「活着」。
      const breathe =
        ph === 'thinking' ? 0.07 + 0.035 * Math.sin(t * 3)
        : (ph === 'idle' || ph === 'ready' || ph === 'connecting') ? 0.03 + 0.02 * Math.sin(t * 1.2)
        : 0;
      const amp = breathe + lvl * 0.5; // 总形变幅度
      const R = baseR * (1 + amp * 0.5);

      // 外层辉光
      const glowR = R * (1.9 + amp);
      const glow = ctx.createRadialGradient(cx, cy, R * 0.4, cx, cy, glowR);
      glow.addColorStop(0, `rgba(${rgb}, ${0.30 + amp * 0.3})`);
      glow.addColorStop(1, `rgba(${rgb}, 0)`);
      ctx.fillStyle = glow;
      ctx.beginPath(); ctx.arc(cx, cy, glowR, 0, Math.PI * 2); ctx.fill();

      // 球体:多频 sine 近似噪声,调制闭合路径的半径 → 有机「液态」形变。
      const N = 96;
      ctx.beginPath();
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * Math.PI * 2;
        const wob =
          Math.sin(a * 3 + t * 1.5) * 0.5 +
          Math.sin(a * 5 - t * 2.1) * 0.3 +
          Math.sin(a * 2 + t * 0.7) * 0.4;
        const rr = R * (1 + wob * (0.05 + amp * 0.18));
        const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      // 高光偏左上,底部收深 → 云球体积感。
      const body = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.3, R * 0.1, cx, cy, R * 1.15);
      body.addColorStop(0, 'rgba(255,255,255,0.92)');
      body.addColorStop(0.42, `rgba(${rgb}, 0.95)`);
      body.addColorStop(1, `rgba(${rgb}, 0.5)`);
      ctx.fillStyle = body;
      ctx.fill();

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [size, micAnalyserRef, outAnalyserRef]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: 'block' }} />;
}
